import React, { useState } from "react";
import { Table, Button, Tag, Modal, Space, Input, message } from "antd";
import {
  ExclamationCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import "./Screens.css";

const suspiciousActivitiesData = [
  {
    id: "ACT001",
    user: "John Doe",
    activity: "Multiple failed login attempts",
    ip: "192.168.1.42",
    date: "2024-02-10 09:15:23",
    severity: "High",
  },
  {
    id: "ACT002",
    user: "Sarah Wilson",
    activity: "Password changed from new IP",
    ip: "203.0.113.15",
    date: "2024-02-09 17:45:10",
    severity: "Medium",
  },
  {
    id: "ACT003",
    user: "Mike Johnson",
    activity: "Unusual transaction pattern detected",
    ip: "198.51.100.22",
    date: "2024-02-08 12:22:47",
    severity: "High",
  },
];

const securityLogsData = [
  {
    id: "LOG001",
    user: "John Doe",
    action: "Login",
    ip: "192.168.1.42",
    date: "2024-02-10 08:50:01",
  },
  {
    id: "LOG002",
    user: "Sarah Wilson",
    action: "Password Change",
    ip: "203.0.113.10",
    date: "2024-01-29 14:30:37",
  },
  {
    id: "LOG003",
    user: "Mike Johnson",
    action: "Logout",
    ip: "198.51.100.22",
    date: "2024-02-08 12:00:00",
  },
];

const blockedIPsData = [
  {
    id: "IP001",
    ip: "203.0.113.100",
    reason: "Suspicious activity",
    dateBlocked: "2024-02-01",
  },
  {
    id: "IP002",
    ip: "198.51.100.200",
    reason: "Multiple failed logins",
    dateBlocked: "2024-02-05",
  },
];

export default function Security() {
  const [blockedIPs, setBlockedIPs] = useState(blockedIPsData);
  const [blockIpModalOpen, setBlockIpModalOpen] = useState(false);
  const [newIpToBlock, setNewIpToBlock] = useState("");
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);

  // Columns for Suspicious Activities Table
  const suspiciousColumns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Activity",
      dataIndex: "activity",
      key: "activity",
      ellipsis: true,
    },
    {
      title: "IP Address",
      dataIndex: "ip",
      key: "ip",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (val) => {
        let color = "default";
        if (val === "High") color = "red";
        else if (val === "Medium") color = "orange";
        else color = "green";
        return <Tag color={color}>{val}</Tag>;
      },
    },
  ];

  // Columns for Security Logs Table
  const logsColumns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
    },
    {
      title: "IP Address",
      dataIndex: "ip",
      key: "ip",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
  ];

  // Columns for Blocked IPs Table
  const blockedIpColumns = [
    {
      title: "IP Address",
      dataIndex: "ip",
      key: "ip",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
    },
    {
      title: "Date Blocked",
      dataIndex: "dateBlocked",
      key: "dateBlocked",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          icon={<UnlockOutlined />}
          size="small"
          danger
          onClick={() => unblockIp(record.ip)}
          title="Unblock IP"
        >
          Unblock
        </Button>
      ),
    },
  ];

  // Handler to unblock IP
  const unblockIp = (ip) => {
    Modal.confirm({
      title: `Unblock IP ${ip}?`,
      icon: <ExclamationCircleOutlined />,
      onOk() {
        setBlockedIPs((prev) => prev.filter((entry) => entry.ip !== ip));
        message.success(`IP ${ip} unblocked`);
      },
    });
  };

  // Handler to open block IP modal
  const openBlockIpModal = () => {
    setNewIpToBlock("");
    setBlockIpModalOpen(true);
  };

  // Confirm block IP
  const handleBlockIp = () => {
    if (!newIpToBlock.trim()) {
      message.error("Please enter an IP address to block");
      return;
    }
    if (blockedIPs.find((entry) => entry.ip === newIpToBlock.trim())) {
      message.warning("IP is already blocked");
      return;
    }
    setBlockedIPs([
      ...blockedIPs,
      {
        id: `IP${blockedIPs.length + 1}`,
        ip: newIpToBlock.trim(),
        reason: "Manually blocked",
        dateBlocked: new Date().toISOString().slice(0, 10),
      },
    ]);
    setBlockIpModalOpen(false);
    message.success(`IP ${newIpToBlock.trim()} blocked successfully`);
  };

  // Compliance Report generation simulation
  const openComplianceModal = () => {
    setComplianceModalOpen(true);
  };

  return (
    <div className="um-root">
      <div className="um-header-row">
        <div>
          <h1 className="um-title">Security Management</h1>
          <span className="um-description">
            Monitor security incidents and manage compliance
          </span>
        </div>
        <Space>
          <Button icon={<FilePdfOutlined />} onClick={openComplianceModal}>
            Generate Compliance Report
          </Button>
          <Button type="primary" onClick={openBlockIpModal}>
            Block IP
          </Button>
        </Space>
      </div>

      <div className="um-table-card" style={{ marginBottom: 24 }}>
        <h2 className="txn-table-title" style={{ marginBottom: 12 }}>
          Suspicious Activities
        </h2>
        <Table
          columns={suspiciousColumns}
          dataSource={suspiciousActivitiesData}
          rowKey="id"
          pagination={{ pageSize: 5 }}
        />
      </div>

      <div className="um-table-card" style={{ marginBottom: 24 }}>
        <h2 className="txn-table-title" style={{ marginBottom: 12 }}>
          Security Logs & Audit Trails
        </h2>
        <Table
          columns={logsColumns}
          dataSource={securityLogsData}
          rowKey="id"
          pagination={{ pageSize: 5 }}
        />
      </div>

      <div className="um-table-card">
        <h2 className="txn-table-title" style={{ marginBottom: 12 }}>
          Blocked IP Addresses
        </h2>
        <Table
          columns={blockedIpColumns}
          dataSource={blockedIPs}
          rowKey="id"
          pagination={false}
        />
      </div>

      {/* Block IP Modal */}
      <Modal
        title="Block an IP Address"
        open={blockIpModalOpen}
        onCancel={() => setBlockIpModalOpen(false)}
        onOk={handleBlockIp}
        okText="Block IP"
        centered
        width={420}
        destroyOnClose
      >
        <Input
          placeholder="Enter IP address"
          value={newIpToBlock}
          onChange={(e) => setNewIpToBlock(e.target.value)}
          autoFocus
          allowClear
        />
      </Modal>

      {/* Compliance Report Modal */}
      <Modal
        title="Compliance Report"
        open={complianceModalOpen}
        onCancel={() => setComplianceModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setComplianceModalOpen(false)}>
            Close
          </Button>,
          <Button key="download" type="primary" icon={<FilePdfOutlined />}>
            Download PDF
          </Button>,
        ]}
        centered
        width={600}
        destroyOnClose
      >
        <p>This is a placeholder for compliance report generation.</p>
        <p>The report includes:</p>
        <ul>
          <li>Summary of security incidents</li>
          <li>IP block actions</li>
          <li>Audit trail highlights</li>
          <li>Compliance status</li>
        </ul>
      </Modal>
    </div>
  );
}
